<template>
    <form-group
        v-bind="form_group_props"
    >
        <template v-slot:read_only>
            <slot name="read_only">
                <label
                    v-if="has_label"
                    class="uk-form-label"
                    :for="identifier"
                >
                    <slot name="label"></slot>
                </label>
                <label v-if="!value || !value.length" class="uk-form-label uk-background-disabled">
                    &nbsp;
                </label>
                <span
                    v-for="selection in value"
                    :key="selection[idField]"
                >
                    <router-link
                        v-if="!!entity"
                        :to="{ name: `${entity}.edit`, params: { id: selection[idField] } }"
                        target="_blank"
                    >
                        {{ selection[labelField] }}
                    </router-link>
                    <span v-else class="uk-badge">{{ selection[labelField] }}</span>
                </span>
            </slot>
        </template>
        <template v-slot:label><slot name="label"></slot></template>
        <template v-slot:hint><slot name="hint"></slot></template>
        <v-select
            ref="field"
            :id="identifier"
            :name="name"
            :class="{'uk-form-warning': (required && !value[name]), 'uk-form-small': isSmall, [`uk-background-${color}`]: true}"
            :value="value"
            :options="options"
            :disabled="disabled"
            :placeholder="placeholder"
            :placeholder-as-hint="placeholderAsHint"
            :debounce="250"
            :label-key="labelField"
            multiple
            :client-side-search="!!localOptions"
            :search-on-empty="true"
            :allow-empty="true"
            :push-tags="canAdd"
            :on-search="search"
            max-height="200px"
            @input="pick"
        >
            <template v-slot:selected-option="{ option }">
                <slot name="selected-option" :option="option">
                    <router-link
                        v-if="!!entity"
                        :to="{ name: `${entity}.edit`, params: { id: option } }"
                        target="_blank"
                    >
                        {{ option_by_id(option)[labelField] }}
                    </router-link>
                    <span v-else>{{ option_by_id(option)[labelField] }}</span>
                </slot>
            </template>
        </v-select>
    </form-group>
</template>

<script>
import { mapGetters } from "vuex"

import is_nibnut_component from "../mixins/IsNibnutComponent"

import FormGroup from "./FormGroup"
import vSelect from "vue-select"

export default {
    name: "FormTagsInput",
    mixins: [is_nibnut_component],
    components: {
        FormGroup,
        vSelect
    },
    methods: {
        option_by_id (option_id) {
            return this.raw_options.find(option => option[this.idField] === option_id) || {}
        },
        search (query) {
            if(!this.localOptions) {
                this.$store.dispatch("AUTOSUGGEST", { entity: this.entity, context: this.name, query: { ...this.contextData, query } }).then(options => {
                    this.option_ids = options.map(option => option[this.idField])
                }).catch(error => {
                    this.$error(error.message)
                })
            } else {
                const filter = (option) => {
                    if(typeof (option) === "object") {
                        return option[this.labelField].toLowerCase().indexOf(query.toLowerCase()) > -1
                    }
                    return option.toLowerCase().indexOf(query.toLowerCase()) > -1
                }
                this.option_ids = this.localOptions.filter(filter).map(option => option[this.idField])
            }
        },
        pick (selection) {
            if(this.editable) {
                if(this.canAdd) {
                    this.$emit("input", selection, this.name)
                } else {
                    this.$emit("input", selection.map(selection => {
                        if(typeof (selection) === "object") return selection[this.idField]
                        return selection
                    }), this.name)
                }
            }
        }
    },
    computed: {
        ...mapGetters(["entity_records"]),
        has_label () {
            return this.has_slot("label")
        },
        raw_options () {
            if(!this.localOptions) return this.entity_records(this.entity)
            return this.localOptions
        },
        options () {
            if(!this.value) return this.raw_options.filter(option => (this.option_ids.indexOf(option[this.idField]) >= 0))
            if(!this.localOptions) return this.raw_options.filter(option => (this.value.indexOf(option[this.idField]) >= 0) || (this.option_ids.indexOf(option[this.idField]) >= 0))
            return this.raw_options.filter(option => (this.value.indexOf(option[this.idField]) >= 0) || (this.option_ids.indexOf(option[this.idField]) >= 0))
        },
        form_group_props () {
            return {
                id: this.id,
                name: this.name,
                value: this.value,
                required: this.required,
                editable: this.editable,
                error: this.error,
                waiting: this.saving
            }
        }
    },
    props: {
        id: {
            type: String,
            validator: prop => !!prop
        },
        entity: {
            type: String,
            default: ""
        },
        name: {
            type: String,
            validator: prop => !!prop,
            required: true
        },
        value: {
            default: null
        },
        idField: {
            type: String,
            default: "id"
        },
        labelField: {
            type: String,
            default: "name"
        },
        contextData: {
            type: Object,
            default () {
                return {}
            }
        },
        localOptions: {
            validator: prop => !prop || Array.isArray(prop),
            default () {
                return null
            }
        },
        placeholder: {
            type: String,
            default: "Pick any number of values"
        },
        placeholderAsHint: {
            type: Boolean,
            default: false
        },
        required: {
            type: Boolean,
            required: true
        },
        canAdd: {
            type: Boolean,
            default: false
        },
        editable: {
            type: Boolean,
            default: true
        },
        disabled: {
            type: Boolean,
            default: false
        },
        saving: {
            type: Boolean,
            default: false
        },
        error: {
            type: String,
            default: ""
        },
        isSmall: {
            type: Boolean,
            default: false
        },
        color: {
            type: String,
            default: ""
        }
    },
    data () {
        return {
            option_ids: []
        }
    }
}
</script>
